import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { uploadAvatar as uploadMyAvatar, updateProfile as updateMyProfile } from "@/integrations/backend/profiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { toast } from "@/hooks/use-toast";
import { Info, Loader2, LogOut, Upload, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function ProfileSettingsView() {
  const { user, profile, refetchProfile, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [debuggerEnabled, setDebuggerEnabled] = useState(false);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || "");
      setLastName(profile.last_name || "");
      setBio(profile.bio || "");
      setAvatarUrl(profile.avatar_url || null);
      setDebuggerEnabled(Boolean(profile.debugger_enabled));
    }
  }, [profile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a JPG, PNG, or WEBP image",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    setAvatarFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveAvatar = async () => {
    if (!avatarUrl || !user) return;

    setUploading(true);
    try {
      await updateMyProfile(user.id, { avatar_url: null });

      setAvatarUrl(null);
      setPreviewUrl(null);
      setAvatarFile(null);
      await refetchProfile();

      toast({
        title: "Avatar removed",
        description: "Your profile picture has been removed successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error removing avatar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile || !user) return null;

    const url = await uploadMyAvatar(user.id, avatarFile, avatarUrl || undefined);
    return url;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setUploading(!!avatarFile);

    try {
      let newAvatarUrl = avatarUrl;

      if (avatarFile) {
        newAvatarUrl = await uploadAvatar();
      }

      await updateMyProfile(user.id, {
        first_name: firstName,
        last_name: lastName,
        bio: bio,
        avatar_url: newAvatarUrl,
        debugger_enabled: debuggerEnabled,
      });

      await refetchProfile();

      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully",
      });

      setAvatarFile(null);
      setPreviewUrl(null);
    } catch (error: any) {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const displayAvatarUrl = previewUrl || avatarUrl;
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  return (
    <div className="max-w-3xl mx-auto space-y-6 py-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Profile Settings</h1>
        <p className="text-gray-600">
          Manage your profile information and profile picture
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-gray-800">Personal Information</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Avatar Section */}
            <div className="flex flex-col items-center space-y-4 pb-6 border-b">
              <Avatar className="h-32 w-32 border-4 border-primary/20">
                <AvatarImage src={displayAvatarUrl || undefined} />
                <AvatarFallback className="bg-primary text-black text-2xl font-semibold">
                  {initials || "?"}
                </AvatarFallback>
              </Avatar>

              <div className="flex gap-2">
                <Label htmlFor="avatar-upload" className="cursor-pointer">
                  <Button
                    type="button"
                    variant="default"
                    disabled={uploading}
                    asChild
                  >
                    <span>
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload Photo
                    </span>
                  </Button>
                </Label>
                <Input
                  id="avatar-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                />

                {(avatarUrl || previewUrl) && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleRemoveAvatar}
                    disabled={uploading}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                JPG, PNG or WEBP. Max 5MB.
              </p>
            </div>

            {/* Email (Read-only) */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={profile?.email || ""}
                disabled
                className="bg-muted/50 text-muted-foreground border-border"
              />
            </div>

            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-gray-700 font-medium">
                First Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                maxLength={50}
                placeholder="Enter your first name"
                className="border-input"
              />
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-gray-700 font-medium">
                Last Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                maxLength={50}
                placeholder="Enter your last name"
                className="border-input"
              />
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label htmlFor="bio" className="text-gray-700 font-medium">
                Bio
              </Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="Tell us about yourself..."
                className="border-input resize-none"
              />
              <p className="text-xs text-muted-foreground">
                {bio.length}/500 characters
              </p>
            </div>

            <div className="flex items-center justify-between gap-4 pt-1">
              <div className="flex min-w-0 items-center gap-2">
                <Label htmlFor="debugger-enabled" className="text-gray-700 font-medium shrink-0 cursor-pointer">
                  AI debugger
                </Label>
                <HoverCard openDelay={200} closeDelay={150}>
                  <HoverCardTrigger asChild>
                    <button
                      type="button"
                      className="rounded-full text-muted-foreground outline-none ring-offset-background transition-colors hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      aria-label="What is AI debugger?"
                    >
                      <Info className="h-4 w-4" aria-hidden />
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent align="start" className="w-72 text-sm space-y-2">
                    <p className="text-foreground leading-snug">
                      Turns on extra technical detail in AI features when something goes wrong or you are
                      troubleshooting. Leave it off for the usual experience.
                    </p>
                    <p className="text-xs text-muted-foreground leading-snug">
                      Applies only to your account. Save profile to keep changes.
                    </p>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <Switch
                id="debugger-enabled"
                checked={debuggerEnabled}
                onCheckedChange={setDebuggerEnabled}
                className="shrink-0"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4 flex gap-3">
              <Button type="submit" className="flex-1" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="outline" disabled={loading}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Log Out
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure you want to log out?</AlertDialogTitle>
                    <AlertDialogDescription>
                      You will need to sign in again to access the admin portal.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="text-black border border-gray-300 hover:bg-gray-100">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={signOut} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Log Out
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
